import React, { useState, useEffect, useContext } from 'react';
import { CustomInputBox1, WSButton } from '../Templates.js';
import { FaEdit, FaSave, FaTimes, FaUpload } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { supabase } from '../data/supabaseClient';
import { AuthContext } from '../Provider';
import { UserBadgeGroup } from '../common/UserBadge';
import Spinner from '../common/Spinner';
import './MyProfile.css';

// Default avatars array (imported from our application)
import Favatar1 from '../Media/avatars/Favatar1.jpg';
import Favatar2 from '../Media/avatars/Favatar2.jpg';
import Favatar3 from '../Media/avatars/Favatar3.jpg';
import Mavatar1 from '../Media/avatars/Mavatar1.jpg';
import Mavatar2 from '../Media/avatars/Mavatar2.jpg';
import Mavatar3 from '../Media/avatars/Mavatar3.jpg';
import Ravatar1 from '../Media/avatars/Ravatar1.jpg';

const DEFAULT_AVATARS = [
  { id: 1, url: Favatar1 },
  { id: 2, url: Favatar2 },
  { id: 3, url: Favatar3 },
  { id: 4, url: Mavatar1 },
  { id: 5, url: Mavatar2 },
  { id: 6, url: Mavatar3 },
  { id: 7, url: Ravatar1 }
];

const UserInfo = ({ 
  profile, 
  onUpdateProfile, 
  onUpdateAvatar, 
  validationErrors,
  isLoading 
}) => {
  const { t } = useTranslation('common');
  const { refreshProfile } = useContext(AuthContext);
  const [editMode, setEditMode] = useState(false);
  const [newUsername, setNewUsername] = useState(profile?.username || "");
  const [showAvatarOptions, setShowAvatarOptions] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [userBadges, setUserBadges] = useState([]);

  // Fetch user's badges
  useEffect(() => {
    const fetchUserBadges = async () => {
      if (!profile?.id) return;
      
      try {
        const { data: badgesData, error } = await supabase
          .from('user_badges')
          .select('badge_type')
          .eq('user_id', profile.id)
          .eq('is_active', true)
          .order('earned_at', { ascending: false })
          .limit(3); // Only show top 3 badges
          
        if (!error && badgesData) {
          setUserBadges(badgesData.map(b => b.badge_type));
        }
      } catch (err) {
        console.error('Error fetching user badges:', err);
      }
    };
    
    fetchUserBadges();
  }, [profile?.id]);
  
  const handleEditClick = () => {
    setEditMode(true);
    setNewUsername(profile?.username || "");
  };
  
  const handleSaveUsername = async () => {
    if (!newUsername.trim()) return;
    
    try {
      await onUpdateProfile({ username: newUsername });
      
      // Refresh context after username change
      await refreshProfile();
      
      setEditMode(false);
    } catch (error) {
      console.error('Error saving username:', error);
    }
  };
  
  const handleAvatarUpload = async (event) => {
    try {
      setUploadingAvatar(true);
      const file = event.target.files[0];
      if (!file) return;
      
      await onUpdateAvatar(file);
      setShowAvatarOptions(false);
    } catch (error) {
      console.error('Error uploading avatar:', error);
    } finally {
      setUploadingAvatar(false);
    }
  };
  
  const handleDefaultAvatarSelect = async (avatarUrl) => {
    try {
      setUploadingAvatar(true);
      await onUpdateAvatar(avatarUrl, 'url');
      setShowAvatarOptions(false);
    } catch (error) {
      console.error('Error setting avatar:', error);
    } finally {
      setUploadingAvatar(false);
    }
  };
  
  // Country management moved to the Settings page (SettingsForm.js).
  
  // Removed handleRemoveAvatar function - users should always have an avatar

  return (
    <div className="User-databox">
      {/* Avatar Management */}
      <div className="avatar-management">
        <div className="avatar-container">
          {profile?.avatar_url ? (
            <img 
              src={profile.avatar_url} 
              alt={t('profile.profile_picture')}
              className="profile-avatar"
              onClick={() => setShowAvatarOptions(!showAvatarOptions)}
            />
          ) : (
            <div 
              className="avatar-placeholder"
              onClick={() => setShowAvatarOptions(!showAvatarOptions)}
            >
              {profile?.username?.charAt(0)?.toUpperCase() || '?'}
            </div>
          )}
        </div>
        
        {showAvatarOptions && (
          <div className="avatar-options-panel">
            <div className="avatar-options-buttons">
              <div className="avatar-upload">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarUpload}
                  id="avatar-upload"
                  disabled={uploadingAvatar}
                  style={{ display: 'none' }}
                />
                <WSButton
                  variant="primary"
                  label={uploadingAvatar ? t('app.uploading') : t('buttons.upload')}
                  onClick={() => document.getElementById('avatar-upload').click()}
                  disabled={uploadingAvatar}
                  icon={uploadingAvatar ? <Spinner size="small" showText={false} thickness={2} /> : <FaUpload />}
                />
              </div>

              <WSButton
                variant="icon"
                onClick={() => setShowAvatarOptions(false)}
                disabled={uploadingAvatar}
                ariaLabel={t('buttons.close') || 'Close'}
                icon={<FaTimes />}
              />
            </div>
            
            <div className="default-avatars-grid">
              {DEFAULT_AVATARS.map((avatar) => (
                <div 
                  key={avatar.id} 
                  className="default-avatar-item"
                  onClick={() => handleDefaultAvatarSelect(avatar.url)}
                >
                  <img 
                    src={avatar.url} 
                    alt={`${t('profile.avatar')} ${avatar.id}`}
                    className="default-avatar-preview"
                  />
                </div>
              ))}
            </div>
            
            {/* Removed remove avatar option - users should always have an avatar */}
          </div>
        )}
      </div>

      <div>
        <div className="profile-header-info">
          <h2>{t('profile.welcome')} {profile?.full_name}</h2>
          {userBadges.length > 0 && (
            <UserBadgeGroup 
              badges={userBadges}
              size="medium"
              maxVisible={3}
              showTooltip={true}
            />
          )}
        </div>
        
        {/* Username section */}
        {editMode ? (
          <div className="username-edit-section">
            <label htmlFor="username-input" className="form-label required">
              {t('profile.visible_name')}
            </label>
            <CustomInputBox1
              className="wsform-input"
              id="username-input"
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
              placeholder={t('profile.visible_name')}
            />
            {validationErrors?.username && (
              <div className="validation-error">{validationErrors.username}</div>
            )}
            <div className="edit-buttons">
              <WSButton
                variant="icon"
                onClick={handleSaveUsername}
                disabled={isLoading}
                ariaLabel={t('buttons.save')}
                icon={isLoading ? <Spinner size="small" showText={false} thickness={2} /> : <FaSave />}
              />
              <WSButton
                variant="icon"
                onClick={() => setEditMode(false)}
                disabled={isLoading}
                ariaLabel={t('buttons.cancel')}
                icon={<FaTimes />}
              />
            </div>
          </div>
        ) : (
          <div className="username-display">
            <h4>{t('profile.visible_name')}: {profile?.username || t('profile.not_set')}</h4>
            <WSButton
              variant="icon"
              onClick={handleEditClick}
              ariaLabel={t('buttons.edit') || 'Edit'}
              icon={<FaEdit />}
            />
          </div>
        )}
        {/* Country management moved to the Settings page. */}
      </div>
    </div>
  );
};

export default UserInfo;